import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

function formatPrice(p) {
  const symbol = { GBP: "£", USD: "$", EUR: "€" }[p.currency] || "£";
  return `${symbol}${Number(p.price).toFixed(0)}`;
}

export default function ProgramDetail() {
  const { id } = useParams();
  const [program, setProgram] = useState(null);

  useEffect(() => {
    api
      .get(`/programs/${id}`)
      .then((r) => setProgram(r.data))
      .catch(() => toast.error("Program not found"));
  }, [id]);

  if (!program) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <p className="text-[#5C5C5C]">Loading…</p>
      </div>
    );
  }

  return (
    <div data-testid="program-detail-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <SEO
        title={`${program.title} — ${program.tagline}`}
        description={program.description}
        path={`/programs/${program.id}`}
        image={program.image_url}
        type="product"
      />
      <Link
        to="/programs"
        className="inline-flex items-center gap-2 text-sm text-[#3A3A3A] hover:text-[#E8A6B8] mb-8 transition"
        data-testid="back-to-programs"
      >
        <ArrowLeft size={16} /> Back to programs
      </Link>
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-6 relative rounded-[2rem] overflow-hidden">
          <img
            src={program.image_url}
            alt={`${program.title} — ${program.tagline}`}
            loading="lazy"
            className="w-full h-full object-cover"
          />
          <span
            className="absolute top-5 right-5 bg-[#3A3A3A] text-white rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold"
            data-testid={`detail-coming-soon-${program.id}`}
          >
            Coming Soon
          </span>
        </div>
        <div className="lg:col-span-6">
          <p className="eyebrow mb-3">{program.tagline}</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            {program.title}
          </h1>
          <p className="mt-6 text-[#5C5C5C] leading-relaxed text-lg">{program.description}</p>

          {/* Pre-launch notice */}
          <div
            className="mt-8 flex items-start gap-3 bg-white border border-[#F3C9D3]/60 rounded-2xl px-5 py-4 shadow-[0_8px_22px_rgba(232,166,184,0.12)]"
            data-testid="program-prelaunch-notice"
            role="status"
          >
            <Sparkles size={16} className="text-[#E8A6B8] flex-shrink-0 mt-0.5" strokeWidth={1.6} />
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A3A3A]">
                Currently Being Refined For Launch
              </p>
              <p className="mt-1 text-sm text-[#5C5C5C] leading-snug">
                The final program is being carefully prepared. Join the waitlist for founding-member pricing and first access.
              </p>
            </div>
          </div>

          <p className="mt-8 font-serif-display text-4xl text-[#3A3A3A]">
            {formatPrice(program)}
            <span className="text-base text-[#5C5C5C] ml-3 font-body italic">at launch</span>
          </p>
          {program.features?.length > 0 && (
            <ul className="mt-8 space-y-3">
              {program.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-[#3A3A3A]">
                  <Check size={18} className="text-[#E8A6B8] mt-0.5" strokeWidth={2} />
                  {f}
                </li>
              ))}
            </ul>
          )}
          <Link
            to="/booking"
            className="btn-pink mt-10"
            data-testid="program-detail-waitlist"
          >
            <Sparkles size={16} /> Join The Waitlist <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
